"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { forceLogout } from "@/lib/auth";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { buttonVariants } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { ModeToggle } from "./mode-toggle";
import { LogoIcon } from "./Icons";
import Link from "next/link";
import { useAuth } from "@/hooks/useAuth";
import { useRouter } from "next/navigation";
import { useTranslation } from "@/hooks/useLanguage";
import { useRtl } from "@/hooks/useRtl";
import { LanguageSwitcher } from "@/components/language-switcher";

export const Navbar = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const { user, isAuthenticated } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const { isRtl } = useRtl();

  const routeList = [
    { href: "/#about", label: t("route.aboutUs") },
    { href: "/#features", label: t("common.features") },
    { href: "/#pricing", label: t("common.pricing") },
    { href: "/#faq", label: t("common.faq") },
    ...(isAuthenticated ? [] : [{ href: "/register", label: t("common.register") }]),
  ];


  const getInitials = (name) => {
    const names = name.trim().split(" ").filter(Boolean);
    if (names.length === 0) return "?";
    return names.length === 1
      ? names[0][0].toUpperCase()
      : `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
  };

  const isActive = (href) => {
    if (href === "/") {
      return pathname === "/";
    }
    return pathname.startsWith(href) && href !== "/";
  };

  const renderUserAvatar = (isMobile = false) => {
    const classes = `w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
      isMobile ? "mt-4" : ""
    } cursor-pointer`;

    const displayName =
      user?.displayName ||
      [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
      user?.name ||
      "";

    return user?.image || user?.profileImageUrl ? (
      <img
        src={user.image || user.profileImageUrl}
        alt={displayName || t("common.user")}
        className={`w-10 h-10 rounded-full object-cover ${
          isMobile ? "mt-4" : ""
        } cursor-pointer`}
      />
    ) : (
      <div className={`bg-gray-400 ${classes}`}>
        {getInitials(displayName)}
      </div>
    );
  };

  return (
    <header className="sticky border-b-[1px] top-0 z-40 w-full bg-white dark:border-b-slate-700 dark:bg-background">
      <NavigationMenu className="mx-auto">
        <NavigationMenuList className="container h-14 px-4 w-screen flex justify-between items-center">
          {/* Logo */}
          <NavigationMenuItem className="font-bold flex">
            <Link
              rel="noreferrer noopener"
              href="/"
              className="ms-2 font-bold text-xl flex items-center gap-2"
            >
              <LogoIcon />
            </Link>
          </NavigationMenuItem>

          {/* Mobile */}
          <span className="flex md:hidden gap-2 items-center">
            <LanguageSwitcher />

            <ModeToggle />

            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger className="px-2">
                <Menu
                  className="flex md:hidden h-5 w-5"
                  onClick={() => setIsOpen(true)}
                />
              </SheetTrigger>

              <SheetContent side={isRtl ? "right" : "left"}>
                <SheetHeader>
                  <SheetTitle className="font-bold text-xl">
                    {t("common.pms")}
                  </SheetTitle>
                </SheetHeader>

                <nav className="flex flex-col justify-center items-center gap-2 mt-4">
                  {routeList.map(({ href, label }) => (
                    <Link
                      key={label}
                      href={href}
                      onClick={() => setIsOpen(false)}
                      className={`${buttonVariants({ variant: "ghost" })} ${
                        isActive(href)
                          ? "relative"
                          : "text-gray-600 dark:text-gray-300"
                      }`}
                    >
                      {label}
                    </Link>
                  ))}

                  {/* Mobile User Dropdown */}
                  {isAuthenticated ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        {renderUserAvatar()}
                      </DropdownMenuTrigger>
                      <DropdownMenuContent sideOffset={4} className="w-56">
                        {/* User info */}
                        <div className="flex flex-col items-start px-4 py-2">
                          <span className="text-sm font-medium">
                            {user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.name}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {user?.email}
                          </span>
                        </div>

                        <DropdownMenuSeparator />

                        {/* Menu items */}
                        <DropdownMenuItem asChild>
                          <Link href="/dashboard">{t("common.my_profile")}</Link>
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => forceLogout()}>
                          Log out
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Link
                      href="/auth"
                      className={`w-[110px] border ${buttonVariants({
                        variant: "secondary",
                      })}`}
                    >
                      {t("common.login")}
                    </Link>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </span>

          {/* Desktop */}
          <nav className="hidden md:flex gap-2">
            {routeList.map((route, i) => (
              <Link
                key={i}
                href={route.href}
                className={`text-[17px] ${buttonVariants({
                  variant: "ghost",
                })} ${
                  isActive(route.href)
                    ? "relative "
                    : "text-gray-600 dark:text-gray-300"
                }`}
              >
                {route.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex gap-4 items-center">
            <LanguageSwitcher />

            {/* Desktop User Dropdown */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  {renderUserAvatar()}
                </DropdownMenuTrigger>
                <DropdownMenuContent sideOffset={4} className="w-56">
                  {/* User info */}
                  <div className="flex flex-col items-start px-4 py-2">
                    <span className="text-sm font-medium">
                      {user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {user?.email}
                    </span>
                  </div>

                  <DropdownMenuSeparator />

                  {/* Menu items */}
                  <DropdownMenuItem asChild>
                    <Link href="/dashboard/profile">
                      {t("common.my_profile")}
                    </Link>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => forceLogout()}>
                    {t("route.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                href="/auth"
                className={`w-[110px] border ${buttonVariants({
                  variant: "secondary",
                })}`}
              >
                {t("common.login")}
              </Link>
            )}

            <ModeToggle />
          </div>
        </NavigationMenuList>
      </NavigationMenu>
    </header>
  );
};
